import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/anime";

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;

  const go = (index: number) => setActive((index + slides.length) % slides.length);
  const current = slides[active];

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.cover}
              alt={`${slide.title} 焦点图`}
              title={`${slide.title} - 78动漫焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8">
            <div className="mx-auto max-w-7xl">
              <div key={current.id} className="max-w-2xl animate-fade-up">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-flashlight-fill" />
                  {current.category} · 焦点推荐
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl lg:text-5xl">
                  {current.title}
                </h2>
                <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {current.summary}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/news/detail/${current.newsId}`}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                  >
                    阅读全文
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                  <Link
                    to="/news"
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
                  >
                    更多资讯
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-0 right-0">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-8">
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => go(index)}
                  aria-label={`切换到第 ${index + 1} 张`}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
                  }`}
                />
              ))}
            </div>
            <div className="hidden items-center gap-2 md:flex">
              <button
                type="button"
                onClick={() => go(active - 1)}
                aria-label="上一张"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/25 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-lg" />
              </button>
              <button
                type="button"
                onClick={() => go(active + 1)}
                aria-label="下一张"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 bg-background-50/10 text-background-50 transition-colors hover:bg-background-50/25 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-lg" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}